/**
 * Reading a batch of operator lookups into something a screen can say.
 *
 * The honest framing matters here, because it is easy to oversell. `/api/def`
 * answers **which operator a number belongs to**, not whether anybody is
 * holding it: an unassigned number inside a real MegaCom range comes back
 * `known`. So this catches numbers the operator cannot parse and numbers
 * outside any Kyrgyz range — typos, foreign numbers, truncated cells — and it
 * does not catch a number that was disconnected last week.
 *
 * That is still worth doing before a ten thousand row campaign, and it is worth
 * not claiming more than it does.
 */

export interface LookupAnswer {
  readonly phone: string;
  readonly outcome: 'known' | 'unknown' | 'malformed' | 'refused' | 'unavailable';
  readonly operator?: string;
  readonly messageKey: string;
}

export interface LookupProblem {
  readonly phone: string;
  readonly messageKey: string;
}

export interface LookupSummary {
  readonly known: number;
  /** Well-formed and in no range the operator carries. Money spent for nothing. */
  readonly unknown: number;
  /** The operator could not read it as a number at all. */
  readonly malformed: number;
  /** No answer, or the question was refused. Says nothing about the number. */
  readonly failed: number;
  /** Every number worth showing, worst first. */
  readonly problems: readonly LookupProblem[];
  /** Which operators the recognised numbers are on, largest first. */
  readonly operators: readonly { readonly name: string; readonly count: number }[];
  readonly checked: number;
}

export function lookupView(answers: readonly LookupAnswer[]): LookupSummary {
  const counts = { known: 0, unknown: 0, malformed: 0, failed: 0 };
  const problems: LookupProblem[] = [];
  const operators = new Map<string, number>();

  for (const answer of answers) {
    if (answer.outcome === 'known') {
      counts.known += 1;
      const name = (answer.operator ?? '').trim();
      if (name !== '') operators.set(name, (operators.get(name) ?? 0) + 1);
      continue;
    }

    // `unknown` and `malformed` are about the number and are worth showing.
    // `refused` and `unavailable` are about the question, and are counted
    // together as "could not check" — listing them per number would be ten
    // thousand identical rows saying the network was down.
    if (answer.outcome === 'unknown') counts.unknown += 1;
    else if (answer.outcome === 'malformed') counts.malformed += 1;
    else {
      counts.failed += 1;
      continue;
    }

    problems.push({ phone: answer.phone, messageKey: answer.messageKey });
  }

  return {
    ...counts,
    problems,
    operators: [...operators]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)),
    checked: answers.length,
  };
}
